import { useSession } from 'next-auth/react';
import { useMemo } from 'react';

import { UserRoles } from '@/domain/user';

export type UserPermissions = Required<UserRoles>;

export const defaultPermissions: UserPermissions = {
    // user roles
    isCampaignsAdmin: false,
    isMyBotsAdmin: false,
    isAllBotsAdmin: false,
    isAnalyticsAdmin: false,
    isBotEditor: false,
    isTester: false,
    isSttExpert: false,
    isLlmGenerator: false,

    // plans
    isFreemium: false,
    isStandard: false,

    // chat bubbles
    isAnalyticsChatEnabled: false,
    isMChatRoleEnabled: false,

    // user permissions
    isVoicebotCallEnabled: false,
    isRecordingDownloadEnabled: false,
    isRecordingPlaybackEnabled: false,
    isSmsChannelEnabled: false,
    isSmsModuleEnabled: false,
    isCalendarModuleEnabled: false,
    isCustomModuleEnabled: false,
    isCustomModuleEditingEnabled: false,
    isGptModuleEnabled: false,
    isMSearchModuleEnabled: false,
    isMDataChatModuleEnabled: false,
    isRestCalloutModuleEnabled: false,
    isTransferToAgentModuleEnabled: false,
    isBioDataEnabled: false,
    isUndoRedoEnabled: false,
    isLabelEditorEnabled: false,
    isBotIdEditorEnabled: false,
    isChatAnalyticsEnabled: false,
    isCallAnalyticsEnabled: false,
    isSmsAnalyticsEnabled: false,
    isStickerModuleEnabled: false,
    isGenericDashboardEnabled: false,
    isMaxParallelCallsEditEnabled: false,
    isShowNonUtteranceInCallHistory: false,

    // clients
    isDrMax: false,
    isRobotWorld: false,
    isPragueAirport: false,
    isPenam: false,
    isMoContactCenter: false,

    // other
    isOldElastic: false,
    isHgcConnex: false,
};

/**
 * Returns the logged-in user's permissions as non-nullable booleans.
 *  For unauthenticated users are all permissions false
 */
export const useUserPermissions = () => {
    const { data: session, status } = useSession();
    const roles = session?.user?.roles;

    const permissions = useMemo<UserPermissions>(() => {
        if (!roles) {
            return defaultPermissions;
        }
        // Merge onto defaults so roles missing from the session resolve to false
        const merged = { ...defaultPermissions };
        for (const key of Object.keys(defaultPermissions) as Array<
            keyof UserPermissions
        >) {
            merged[key] = !!roles[key];
        }
        return merged;
    }, [roles]);

    return {
        ...permissions,
        isAuthenticated: status === 'authenticated',
    };
};
